import { RouteManager } from '@goodt-wcore/managers';
import { Url } from '@goodt-common/utils';
import { CoreError } from '@goodt-wcore/errors';

const isEmpty = (value) => [undefined, null, ''].includes(value);

export const useNavigate = () => {
    const navigate = ({ url, route }, { isNewWindow = false } = {}) => {
        if ([url, url?.path, route?.path].every(isEmpty)) {
            throw new CoreError(`"url" and "route" param could not be empty ('', null or undefined) at the same time`);
        }

        if (isEmpty(url)) {
            RouteManager.instance.navigate(route);
            return;
        }

        const urlInstance = new Url(url);
        if (urlInstance.isRelative) {
            const { host, port, schema } = window.location;
            urlInstance.schema = schema;
            urlInstance.host = host;
            urlInstance.port = port;
        }

        const finalUrl = isEmpty(route)
            ? urlInstance.toString()
            : RouteManager.instance.buildRouteString(route, urlInstance.toString());

        if (isNewWindow) {
            const opened = window.open(finalUrl, '_blank', 'noopener,noreferrer');
            // https://cwe.mitre.org/data/definitions/1022.html
            // https://owasp.org/www-project-top-ten/2017/A6_2017-Security_Misconfiguration.html
            opened.opener = null;
            return;
        }
        window.location.href = finalUrl;
    };

    const buildNavigateOptions = (url, urlOptions = {}) => {
        const { query: optionQuery, ...restOptions } = urlOptions;
        const urlModel = Url.create({
            href: url,
            ...restOptions
        });

        const hash = urlModel.hash.slice(1); // remove '#'
        const hasRoute = hash.startsWith('/');
        if (!hasRoute) {
            urlModel.query = {
                ...urlModel.query,
                ...optionQuery
            }
            return {
                url: urlModel.toString()
            }
        }
        // exclude 'hash' part from url
        urlModel.hash = '';
        // extract route params
        const { path, query } = Url.create({ href: hash, query: optionQuery });

        return {
            url: urlModel.toString(),
            route: {
                path,
                query
            }
        };
    };

    return { navigate, buildNavigateOptions };
};
